import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import InnerBox from '@repo/ui/inner-box'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import {
  CurrencyEnum,
  currencyEnumMapping,
  currencyEnumOptions
} from '../../../lib/types/enums/common/currency.enum'
import { OLTypeEnum, olTypeEnumMapping } from '../../../lib/types/enums/ol/ol-type.enum'
import { LecBankAccountInput } from '../../../lib/types/models/lec-bank-account/lec-bank-account-input'
import { BaseFormProps, NullableTransformer } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  createEnumArraySchema,
  createEnumSchema
} from '../../../lib/utils/validations/enum-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import { ControlledAdressInput } from '../../elements/inputs/address-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<LecBankAccountInput, true>

export const lecBankAccountFormSchema: ZodType<NullableTransformer<LecBankAccountInput>> =
  z.object({
    bankName: createStringSchema('Nom de la banque'),
    bankAddress: createStringSchema('Adresse de la banque'),
    bankAddress2: createStringSchema('Adresse de la banque (ligne 2)', { nullish: true }),
    bankCity: createStringSchema('Ville de la banque'),
    bankZipCode: createStringSchema('Code postal de la banque'),
    bankCountry: createStringSchema('Pays de la banque'),
    iban: createStringSchema('Iban'),
    bic: createStringSchema('Bic'),
    currency: createEnumSchema({ fieldName: 'Devise', enumType: CurrencyEnum }),
    accountHolder: createStringSchema('Titulaire'),
    accountHolderAddress: createStringSchema('Adresse titulaire'),
    olTypes: createEnumArraySchema({
      fieldName: 'Type de prestataire',
      enumType: OLTypeEnum,
      minimumNumberOfItems: 1
    })
  })

export const LecBankAccountForm = ({ defaultValues, ...validationButtonProps }: Props) => {
  const form = useForm<LecBankAccountInput>({
    resolver: zodResolver(lecBankAccountFormSchema),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <InnerBox className={cn('p-4 rounded-2xl')} title="Compte bancaire de LEC">
          <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
            <FormField
              control={form.control}
              name={'bankName'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      required
                      {...field}
                      type={'text'}
                      label={'Nom de la banque'}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'bankAddress'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledAdressInput
                      required
                      {...field}
                      label="Adresse de la banque"
                      onDetailsChange={(placeDetails) => {
                        form.setValue('bankAddress', placeDetails.address, {
                          shouldValidate: true
                        })
                        form.setValue('bankZipCode', placeDetails.postalCode, {
                          shouldValidate: true
                        })
                        form.setValue('bankCity', placeDetails.city, { shouldValidate: true })
                        form.trigger(['bankAddress', 'bankZipCode', 'bankCity'])
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'bankAddress2'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      type={'text'}
                      label="Complément d'adresse"
                      value={field.value}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex gap-2">
              <FormField
                control={form.control}
                name="bankZipCode"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <ControlledInput
                        {...field}
                        label="Code postal de la banque"
                        type="text"
                        required
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bankCity"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <ControlledInput
                        {...field}
                        label="Ville de la banque"
                        type="text"
                        required
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bankCountry"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <ControlledInput
                        {...field}
                        label={'Pays de la banque'}
                        type="text"
                        required
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name={'iban'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput required {...field} type={'text'} label={'IBAN'} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'bic'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput required {...field} type={'text'} label={'BIC'} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'currency'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledSelectInput
                      required
                      {...field}
                      label={'Devise'}
                      options={currencyEnumOptions}
                      selectedOption={
                        field.value
                          ? { label: currencyEnumMapping[field.value].label, value: field.value }
                          : undefined
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'accountHolder'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      required
                      {...field}
                      type={'text'}
                      label={'Titulaire du compte'}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'accountHolderAddress'}
              render={({ field }) => (
                <FormItem className={'md:col-span-2'}>
                  <FormControl>
                    <ControlledInput
                      required
                      {...field}
                      type={'text'}
                      label={'Adresse du titulaire'}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'olTypes'}
              render={({ field }) => (
                <FormItem className={'md:col-span-2'}>
                  <FormControl>
                    <ControlledMultiSelectInput
                      required
                      {...field}
                      label={'Type de prestataire'}
                      variant={'primary'}
                      options={[OLTypeEnum.LEC, OLTypeEnum.PARTNER, OLTypeEnum.RESIDENCE].map(
                        (t) => ({ value: t, label: olTypeEnumMapping[t].label })
                      )}
                      selectedOptions={field.value?.map((t) => ({
                        value: t,
                        label: olTypeEnumMapping[t].label
                      }))}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </InnerBox>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}
